import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { Users, UserPlus, Phone, Trophy, TrendingUp, Activity, Globe } from 'lucide-react';
import api from '../utils/api';
import { STATUS_CONFIG, formatDate } from '../utils/constants';

export default function Dashboard() {
  const [data, setData] = useState(null);
  const navigate = useNavigate();

  useEffect(() => { api.get('/reports/dashboard').then(res => setData(res.data)).catch(console.error); }, []);
  if (!data) return <div style={{color:'var(--text-muted)',padding:40}}>Cargando dashboard...</div>;

  const { summary, statusBreakdown, sourceBreakdown, recentActivity, datasetStats } = data;

  const statCards = [
    { label: 'Total Leads', value: summary.total_leads, icon: Users, color: '#2E7DE2' },
    { label: 'Nuevos esta semana', value: summary.new_this_week, icon: UserPlus, color: '#a855f7' },
    { label: 'Leads web (semana)', value: summary.web_leads_week, icon: Globe, color: '#22c55e' },
    { label: 'Contactados', value: summary.contacted, icon: Phone, color: '#eab308' },
    { label: 'Ganados', value: summary.ganados, icon: Trophy, color: '#22c55e' },
    { label: 'Conversión', value: `${summary.conversion_rate}%`, icon: TrendingUp, color: '#f97316' },
  ];

  const funnelData = statusBreakdown
    .map(s => ({ name: STATUS_CONFIG[s.status]?.label || s.status, value: s.count, color: STATUS_CONFIG[s.status]?.color }))
    .filter(s => s.value > 0);

  const sourceColors = ['#2E7DE2', '#22c55e', '#f97316', '#a855f7', '#eab308', '#ef4444'];
  const sourceData = (sourceBreakdown || []).filter(s => s.count > 0).map((s, i) => ({
    name: s.source === 'apify' ? 'Apify' : s.source === 'web_form' ? 'Formulario Web' : s.source === 'csv' ? 'CSV' : s.source === 'manual' ? 'Manual' : s.source,
    value: s.count, color: sourceColors[i % sourceColors.length]
  }));

  const tooltipStyle = { background: '#1c2235', border: '1px solid #252d3d', borderRadius: 8, fontFamily: 'Raleway', color: '#e8ecf4' };

  return (
    <div>
      <h1 style={{ fontSize: 26, fontWeight: 800, marginBottom: 24 }}>Dashboard</h1>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 14, marginBottom: 28 }}>
        {statCards.map((s, i) => (
          <div key={i} className="stat-card">
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <span className="stat-value">{s.value}</span>
              <s.icon size={22} color={s.color} />
            </div>
            <span className="stat-label">{s.label}</span>
          </div>
        ))}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 20, marginBottom: 28 }}>
        <div className="card">
          <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 16 }}>Leads por estado</h3>
          {funnelData.length > 0 ? (
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={funnelData} layout="vertical" margin={{ left: 20 }}>
                <XAxis type="number" tick={{ fill: '#c8d0e0', fontSize: 12 }} />
                <YAxis dataKey="name" type="category" width={130} tick={{ fill: '#c8d0e0', fontSize: 12 }} />
                <Tooltip contentStyle={tooltipStyle} />
                <Bar dataKey="value" radius={[0, 6, 6, 0]}>
                  {funnelData.map((entry, i) => <Cell key={i} fill={entry.color} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          ) : <p style={{color:'var(--text-muted)'}}>Sin datos todavía</p>}
        </div>

        <div className="card">
          <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 16 }}>Leads por fuente</h3>
          {sourceData.length > 0 ? (
            <ResponsiveContainer width="100%" height={280}>
              <PieChart>
                <Pie data={sourceData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={90} innerRadius={50} label={({name, percent}) => `${name} ${(percent*100).toFixed(0)}%`}>
                  {sourceData.map((entry, i) => <Cell key={i} fill={entry.color} />)}
                </Pie>
                <Tooltip contentStyle={tooltipStyle} />
              </PieChart>
            </ResponsiveContainer>
          ) : <p style={{color:'var(--text-muted)'}}>Sin datos todavía</p>}
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 20 }}>
        <div className="card">
          <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 16 }}>Bases de datos</h3>
          {datasetStats.length > 0 ? datasetStats.map(ds => (
            <div key={ds.id} onClick={() => navigate(`/datasets/${ds.id}`)}
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 12px', borderRadius: 8, cursor: 'pointer', marginBottom: 4, transition: 'background 0.15s' }}
              onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-hover)'}
              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
              <div>
                <span style={{ fontWeight: 500, fontSize: 14 }}>{ds.name}</span>
                {ds.source_type && <span style={{ fontSize: 10, color: 'var(--text-muted)', marginLeft: 8 }}>({ds.source_type})</span>}
              </div>
              <div style={{ display: 'flex', gap: 8, fontSize: 12, color: 'var(--text-muted)' }}>
                <span>{ds.leads} leads</span>
                {ds.ganados > 0 && <span style={{color:'#22c55e'}}>{ds.ganados} ganados</span>}
              </div>
            </div>
          )) : <p style={{color:'var(--text-muted)'}}>No hay bases importadas</p>}
        </div>

        <div className="card">
          <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 16 }}>Actividad reciente</h3>
          {recentActivity.length > 0 ? recentActivity.slice(0, 8).map(a => (
            <div key={a.id} style={{ padding: '8px 0', borderBottom: '1px solid var(--border)', fontSize: 13 }}>
              <span style={{ color: 'var(--text-secondary)' }}>{a.details}</span>
              <div style={{ color: 'var(--text-muted)', fontSize: 11, marginTop: 2 }}>{formatDate(a.created_at)}</div>
            </div>
          )) : <p style={{color:'var(--text-muted)'}}>Sin actividad todavía</p>}
        </div>
      </div>
    </div>
  );
}
